import { TypedDocumentNode, gql } from "@apollo/client";
import { VideoQuery } from "./types";

const VIDEO_NODE_CORE_FIELDS = `
  id
  title
  description
  url
  series
  publishedAt
  expiredAt
  creditCategory
  durationInSeconds
  assessment {
    id
    questions {
      id
      content
      position
      options {
        id
        content
        answer
        position
      }
    }
  }
`;

/**
 * Identity fields needed even when backoffice-scorm-courses is off so list→detail
 * navigation can recognize SCORM rows and keep them read-only. Status/launch fields
 * stay on the flag-on document only.
 */
const VIDEO_NODE_SCORM_IDENTITY_FIELDS = `
  contentType
  creditAmount
`;

const VIDEO_NODE_SCORM_STATUS_FIELDS = `
  scormStatus
  scormStatusMessage
  scormLaunchUrl
`;

/**
 * Flag-off list query. Requests SCORM identity (`contentType` / `creditAmount`) so
 * row.original retains enough to gate SCORM as read-only, without selecting
 * status/launch fields used only when the flag is on.
 */
export const VIDEOS_QUERY: TypedDocumentNode<VideoQuery> = gql`
  query getMembershipVideos(
    $first: Int
    $last: Int
    $before: String
    $after: String
    $searchTerm: String
    $creditCategories: [MembershipCreditCategory!]
    $stages: [String!]
    $series: [String!]
    $orderBy: [BackofficeMembershipVideoOrderByInput!]
  ) {
    membershipVideos(
      first: $first
      last: $last
      before: $before
      after: $after
      searchTerm: $searchTerm
      creditCategories: $creditCategories
      stages: $stages
      series: $series
      orderBy: $orderBy
    ) {
      nodes {
        ${VIDEO_NODE_CORE_FIELDS}
        ${VIDEO_NODE_SCORM_IDENTITY_FIELDS}
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

/**
 * SCORM-enriched list query. Used when backoffice-scorm-courses is on and
 * MembershipVideoType exposes status/launch fields on coach-tools.
 *
 * Note: `membershipVideos` does not yet accept a `contentTypes` filter arg on
 * the backoffice schema. Do not declare/forward `$contentTypes` here until the
 * backend adds it — undeclared vars are dropped by Apollo, and declaring an
 * unknown field arg would invalidate the whole document.
 */
export const VIDEOS_WITH_SCORM_QUERY: TypedDocumentNode<VideoQuery> = gql`
  query getMembershipVideosWithScorm(
    $first: Int
    $last: Int
    $before: String
    $after: String
    $searchTerm: String
    $creditCategories: [MembershipCreditCategory!]
    $stages: [String!]
    $series: [String!]
    $orderBy: [BackofficeMembershipVideoOrderByInput!]
  ) {
    membershipVideos(
      first: $first
      last: $last
      before: $before
      after: $after
      searchTerm: $searchTerm
      creditCategories: $creditCategories
      stages: $stages
      series: $series
      orderBy: $orderBy
    ) {
      nodes {
        ${VIDEO_NODE_CORE_FIELDS}
        ${VIDEO_NODE_SCORM_IDENTITY_FIELDS}
        ${VIDEO_NODE_SCORM_STATUS_FIELDS}
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export const GET_SERIES_QUERY = gql`
  query GetSeries($live: Boolean) {
    videoSeries(live: $live)
  }
`;
